import * as React from 'react'

import { Badge, type BadgeProps, type BadgeVariant } from '../badge'
import { cn } from '../lib/cn'
import { SimpleTooltip } from '../tooltip'

export interface SplitBadgeProps {
  /** Left half content. When omitted, only the right badge renders (fully rounded). */
  left?: React.ReactNode
  /** Right half content. */
  right: React.ReactNode
  leftVariant?: BadgeVariant
  rightVariant?: BadgeVariant
  /** Optional hover tooltip for each half (e.g. to label two bare values). */
  leftTooltip?: React.ReactNode
  rightTooltip?: React.ReactNode
  size?: BadgeProps['size']
  className?: string
  leftClassName?: string
  rightClassName?: string
}

interface HalfProps {
  content: React.ReactNode
  variant: BadgeVariant
  tooltip?: React.ReactNode
  size: BadgeProps['size']
  className?: string
}

// Badge is not a forwardRef component, so the tooltip trigger wraps a span
// rather than the Badge itself.
const Half = ({ content, variant, tooltip, size, className }: HalfProps) => {
  const badge = (
    <Badge variant={variant} size={size} className={cn('rounded-none border-0', className)}>
      {content}
    </Badge>
  )

  if (!tooltip) return badge

  return (
    <SimpleTooltip content={tooltip} delayDuration={0}>
      <span className='inline-flex'>{badge}</span>
    </SimpleTooltip>
  )
}

// Two Badge halves fused into one pill. Each side keeps its own Badge variant so
// the colors come straight from the shared Badge palette; the rounded, clipped
// wrapper hides the badges' own corners so the seam in the middle stays flat.
export const SplitBadge = ({
  left,
  right,
  leftVariant = 'info',
  rightVariant = 'secondary',
  leftTooltip,
  rightTooltip,
  size = 'tag',
  className,
  leftClassName,
  rightClassName,
}: SplitBadgeProps) => {
  const hasLeft = left != null

  return (
    <span className={cn('inline-flex w-fit items-stretch overflow-hidden rounded-full border border-input', className)}>
      {hasLeft && (
        <Half content={left} variant={leftVariant} tooltip={leftTooltip} size={size} className={leftClassName} />
      )}
      <Half content={right} variant={rightVariant} tooltip={rightTooltip} size={size} className={rightClassName} />
    </span>
  )
}
SplitBadge.displayName = 'SplitBadge'
